import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Platform,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import * as XLSX from "xlsx";

const BACKEND_URL = process.env.BACKEND_URL || "https://sbg-backend.onrender.com";

const FIELD_CONFIG = [
  {
    key: "payRate",
    label: "Pay Rate",
    aliases: [
      "payrate",
      "pay rate",
      "regularpayrate",
      "regular pay rate",
      "stpayrate",
      "st pay rate",
      "straightpayrate",
      "straight time pay rate",
      "hourlyrate",
      "hourly rate",
      "earnings1rate",
      "earnings 1/rate",
      "earnings rate",
    ],
  },
  {
    key: "otPayRate",
    label: "OT Pay Rate",
    aliases: [
      "otpayrate",
      "ot pay rate",
      "overtimepayrate",
      "overtime pay rate",
      "otpay",
      "ot pay",
    ],
  },
  {
    key: "stBillRate",
    label: "ST Bill Rate",
    aliases: [
      "stbillrate",
      "st bill rate",
      "straightbillrate",
      "straight bill rate",
      "straighttimebillrate",
      "straight time bill rate",
      "billrate",
      "bill rate",
      "regularbillrate",
      "regular bill rate",
    ],
  },
  {
    key: "otBillRate",
    label: "OT Bill Rate",
    aliases: [
      "otbillrate",
      "ot bill rate",
      "overtimebillrate",
      "overtime bill rate",
      "otrate",
      "ot rate",
    ],
  },
];

const MATCH_CONFIG = [
  {
    key: "employeeId",
    label: "Employee ID",
    aliases: ["employeeid", "employee id", "empid", "emp id", "id"],
  },
  {
    key: "email",
    label: "Email",
    aliases: ["email", "emailaddress", "email address", "employeeemail", "employee email"],
  },
  {
    key: "employeeName",
    label: "Employee Name",
    aliases: ["employee", "employeename", "employee name", "name", "worker", "worker name"],
  },
];

const normalizeHeader = (value) =>
  `${value ?? ""}`.trim().toLowerCase().replace(/[^a-z0-9]/g, "");

const normalizeText = (value) =>
  `${value ?? ""}`.trim().toLowerCase().replace(/\s+/g, " ");

const cleanNameTokens = (value) =>
  normalizeText(value)
    .replace(/[^a-z,\s]/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .filter((part) => !["jr", "sr", "ii", "iii", "iv"].includes(part));

const normalizeName = (value, { dropMiddle = false } = {}) => {
  const text = normalizeText(value).replace(/[^a-z,\s]/g, "");
  let parts;

  if (text.includes(",")) {
    const [last, first] = text.split(",").map((part) => part.trim());
    parts = [...cleanNameTokens(first), ...cleanNameTokens(last)];
  } else {
    parts = cleanNameTokens(text);
  }

  if (dropMiddle && parts.length > 2) {
    parts = [parts[0], parts[parts.length - 1]];
  }

  return parts.join(" ");
};

const getNameVariants = (value) => {
  const normalized = normalizeName(value);
  const noMiddle = normalizeName(value, { dropMiddle: true });
  const parts = normalized.split(" ").filter(Boolean);
  const variants = new Set([normalized, noMiddle]);

  if (parts.length >= 2) {
    variants.add(`${parts[parts.length - 1]} ${parts[0]}`);
    variants.add(`${parts[0]} ${parts[parts.length - 1]}`);
  }

  return Array.from(variants).filter(Boolean);
};

const normalizeMoney = (value, fieldKey, columnName) => {
  const raw = `${value ?? ""}`.trim();
  if (!raw) {
    return null;
  }

  if (
    fieldKey === "payRate" &&
    /salary/i.test(raw) &&
    /earnings/i.test(`${columnName ?? ""}`)
  ) {
    return null;
  }

  const trailingAmount = raw.match(/(-?\$?[\d,]+(?:\.\d+)?)\s*$/);
  const candidate = trailingAmount ? trailingAmount[1] : raw;
  const cleaned = candidate.replace(/[$,\s]/g, "").trim();
  if (!cleaned) {
    return null;
  }

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatRate = (value) =>
  Number.isFinite(value) ? value.toFixed(2).replace(/\.00$/, "") : "";

const buildHeaderMap = (row) => {
  const headerMap = {};

  Object.keys(row || {}).forEach((header) => {
    headerMap[normalizeHeader(header)] = header;
  });

  return headerMap;
};

const findColumn = (headerMap, aliases) => {
  for (const alias of aliases) {
    const normalizedAlias = normalizeHeader(alias);
    if (headerMap[normalizedAlias]) {
      return headerMap[normalizedAlias];
    }
  }

  return null;
};

const buildEmployeeLookup = (employees) => {
  const byId = new Map();
  const byEmail = new Map();
  const byName = new Map();

  employees.forEach((employee) => {
    if (employee.employeeId) {
      byId.set(normalizeText(employee.employeeId), employee);
    }

    if (employee.email) {
      byEmail.set(normalizeText(employee.email), employee);
    }

    getNameVariants(employee.employeeName).forEach((nameVariant) => {
      if (!byName.has(nameVariant)) {
        byName.set(nameVariant, employee);
      }
    });
  });

  return { byId, byEmail, byName };
};

const getMatchedEmployee = (row, columns, lookup) => {
  const employeeId = columns.employeeId ? normalizeText(row[columns.employeeId]) : "";
  if (employeeId && lookup.byId.has(employeeId)) {
    return { employee: lookup.byId.get(employeeId), matchedBy: "Employee ID" };
  }

  const email = columns.email ? normalizeText(row[columns.email]) : "";
  if (email && lookup.byEmail.has(email)) {
    return { employee: lookup.byEmail.get(email), matchedBy: "Email" };
  }

  const employeeName = columns.employeeName ? row[columns.employeeName] : "";
  for (const nameVariant of getNameVariants(employeeName)) {
    if (lookup.byName.has(nameVariant)) {
      return { employee: lookup.byName.get(nameVariant), matchedBy: "Name" };
    }
  }

  return { employee: null, matchedBy: "" };
};

const getEmployeeByName = (name, lookup) => {
  for (const nameVariant of getNameVariants(name)) {
    if (lookup.byName.has(nameVariant)) {
      return lookup.byName.get(nameVariant);
    }
  }

  return null;
};

const getSheetByName = (workbook, targetName) => {
  const sheetName = workbook.SheetNames.find(
    (name) => normalizeText(name) === normalizeText(targetName)
  );

  return sheetName ? workbook.Sheets[sheetName] : null;
};

const parseGenericRateSheet = (sheet, lookup) => {
  const parsedRows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

  if (!parsedRows.length) {
    return { rows: [], columns: {} };
  }

  const headerMap = buildHeaderMap(parsedRows[0]);
  const detectedColumns = {};

  MATCH_CONFIG.forEach((field) => {
    detectedColumns[field.key] = findColumn(headerMap, field.aliases);
  });

  FIELD_CONFIG.forEach((field) => {
    detectedColumns[field.key] = findColumn(headerMap, field.aliases);
  });

  const hasMatcher =
    detectedColumns.employeeId || detectedColumns.email || detectedColumns.employeeName;
  const hasRateColumn = FIELD_CONFIG.some((field) => detectedColumns[field.key]);

  if (!hasMatcher || !hasRateColumn) {
    return { rows: [], columns: detectedColumns };
  }

  const rows = parsedRows.map((row, index) => {
    const { employee, matchedBy } = getMatchedEmployee(row, detectedColumns, lookup);
    const updates = {};

    FIELD_CONFIG.forEach((field) => {
      const column = detectedColumns[field.key];
      if (!column) {
        return;
      }

      const rate = normalizeMoney(row[column], field.key, column);
      if (rate !== null) {
        updates[field.key] = rate;
      }
    });

    return {
      rowNumber: index + 2,
      sourceName:
        row[detectedColumns.employeeName] ||
        row[detectedColumns.employeeId] ||
        row[detectedColumns.email] ||
        `Row ${index + 2}`,
      employee,
      matchedBy,
      updates,
      updateCount: Object.keys(updates).length,
      sources: ["Uploaded sheet"],
    };
  });

  return { rows, columns: detectedColumns };
};

const parseDetailedSheet = (sheet, lookup) => {
  const parsedRows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

  if (!parsedRows.length) {
    return { rows: [], columns: {} };
  }

  const headerMap = buildHeaderMap(parsedRows[0]);
  const employeeColumn = findColumn(headerMap, ["employee", "employee name", "name"]);
  const rateColumn = findColumn(headerMap, ["earnings 1/rate", "earnings1rate"]);

  if (!employeeColumn || !rateColumn) {
    return { rows: [], columns: {} };
  }

  const rows = parsedRows.map((row, index) => {
    const sourceName = row[employeeColumn];
    const employee = getEmployeeByName(sourceName, lookup);
    const payRate = normalizeMoney(row[rateColumn], "payRate", rateColumn);
    const updates = payRate !== null ? { payRate } : {};

    return {
      rowNumber: `Detailed ${index + 2}`,
      sourceName,
      employee,
      matchedBy: employee ? "Name" : "",
      updates,
      updateCount: Object.keys(updates).length,
      sources: ["Detailed"],
    };
  });

  return {
    rows,
    columns: {
      employeeName: employeeColumn,
      payRate: rateColumn,
    },
  };
};

const parsePayrollSummarySheet = (sheet, lookup) => {
  const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

  if (matrix.length < 3) {
    return { rows: [], columns: {} };
  }

  const namesRow = matrix[0] || [];
  const headerRow = matrix[1] || [];
  const overtimeRowIndex = matrix.findIndex((row) =>
    row.some((cell) => /overtime/i.test(`${cell ?? ""}`))
  );

  if (overtimeRowIndex < 0) {
    return { rows: [], columns: {} };
  }

  const overtimeRow = matrix[overtimeRowIndex];
  const rows = [];

  for (let columnIndex = 0; columnIndex < namesRow.length; columnIndex += 1) {
    const employeeName = namesRow[columnIndex];
    const rateHeader = headerRow[columnIndex + 1];

    if (!employeeName || normalizeHeader(rateHeader) !== "rate") {
      continue;
    }

    const otPayRate = normalizeMoney(overtimeRow[columnIndex + 1], "otPayRate", "Rate");
    if (otPayRate === null) {
      continue;
    }

    const employee = getEmployeeByName(employeeName, lookup);

    rows.push({
      rowNumber: `Payroll Summary ${overtimeRowIndex + 1}`,
      sourceName: employeeName,
      employee,
      matchedBy: employee ? "Name" : "",
      updates: { otPayRate },
      updateCount: 1,
      sources: ["Employee Payroll Summary"],
    });
  }

  return {
    rows,
    columns: {
      employeeName: "Employee Payroll Summary employee columns",
      otPayRate: "Overtime (x1.5) hourly > Rate",
    },
  };
};

const mergeImportRows = (importRows) => {
  const rowMap = new Map();

  importRows.forEach((row) => {
    const key = row.employee
      ? `employee:${row.employee.employeeId}`
      : `source:${normalizeName(row.sourceName)}:${row.rowNumber}`;
    const existing = rowMap.get(key);

    if (!existing) {
      rowMap.set(key, { ...row, sources: [...(row.sources || [])] });
      return;
    }

    rowMap.set(key, {
      ...existing,
      updates: {
        ...existing.updates,
        ...row.updates,
      },
      updateCount: Object.keys({ ...existing.updates, ...row.updates }).length,
      rowNumber: `${existing.rowNumber}, ${row.rowNumber}`,
      sources: Array.from(new Set([...(existing.sources || []), ...(row.sources || [])])),
    });
  });

  return Array.from(rowMap.values());
};

const QuickBooksImport = () => {
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [processingFile, setProcessingFile] = useState(false);
  const [applying, setApplying] = useState(false);
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState({});
  const [results, setResults] = useState(null);

  const employeeLookup = useMemo(() => buildEmployeeLookup(employees), [employees]);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const response = await fetch(`${BACKEND_URL}/employees`);
      if (!response.ok) {
        throw new Error("Failed to load employees");
      }

      const data = await response.json();
      setEmployees(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("QuickBooks employee load error:", error);
      Alert.alert("Error", "Unable to load employees for matching.");
    } finally {
      setLoadingEmployees(false);
    }
  };

  const chooseFile = () => {
    if (Platform.OS !== "web") {
      Alert.alert("Web Only", "QuickBooks Excel upload is available in the web admin app.");
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx,.xls,.csv";
    input.onchange = (event) => {
      const file = event.target.files?.[0];
      if (file) {
        readWorkbook(file);
      }
    };
    input.click();
  };

  const readWorkbook = async (file) => {
    try {
      setProcessingFile(true);
      setFileName(file.name);
      setResults(null);

      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const detailedSheet = getSheetByName(workbook, "Detailed");
      const payrollSummarySheet = getSheetByName(workbook, "Employee Payroll Summary");
      const importedRows = [];
      const detectedColumns = {};

      if (detailedSheet) {
        const detailedImport = parseDetailedSheet(detailedSheet, employeeLookup);
        importedRows.push(...detailedImport.rows);
        Object.assign(detectedColumns, detailedImport.columns);
      }

      if (payrollSummarySheet) {
        const payrollSummaryImport = parsePayrollSummarySheet(
          payrollSummarySheet,
          employeeLookup
        );
        importedRows.push(...payrollSummaryImport.rows);
        Object.assign(detectedColumns, payrollSummaryImport.columns);
      }

      if (!importedRows.length) {
        const genericImport = parseGenericRateSheet(
          workbook.Sheets[workbook.SheetNames[0]],
          employeeLookup
        );
        importedRows.push(...genericImport.rows);
        Object.assign(detectedColumns, genericImport.columns);
      }

      if (!importedRows.length) {
        throw new Error(
          "No importable employee rate data was found. Use the Detailed tab for base pay rates and Employee Payroll Summary for OT pay rates."
        );
      }

      setColumns(detectedColumns);
      setRows(mergeImportRows(importedRows));
    } catch (error) {
      console.error("QuickBooks parse error:", error);
      Alert.alert("Import Error", error.message || "Unable to read the QuickBooks file.");
      setFileName("");
      setRows([]);
      setColumns({});
    } finally {
      setProcessingFile(false);
    }
  };

  const rowsToUpdate = rows.filter((row) => row.employee && row.updateCount > 0);
  const unmatchedRows = rows.filter((row) => !row.employee);
  const noRateRows = rows.filter((row) => row.employee && row.updateCount === 0);

  const applyUpdates = async () => {
    if (!rowsToUpdate.length) {
      Alert.alert("No Updates", "There are no matched rows with rate values to apply.");
      return;
    }

    try {
      setApplying(true);
      const summary = {
        updated: [],
        failed: [],
      };

      for (const row of rowsToUpdate) {
        const employee = row.employee;
        const payload = {
          ...employee,
          ...row.updates,
          department: employee.department || "",
          hireDate: employee.hireDate || null,
          supervisors: Array.isArray(employee.supervisors) ? employee.supervisors : [],
          supervisorId: employee.supervisorId || employee.supervisors?.[0]?.id || "",
          isSpecialFormatEmployee: employee.isSpecialFormatEmployee === true,
        };

        try {
          const response = await fetch(
            `${BACKEND_URL}/editEmployeeWithSupervisors/${employee.employeeId}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(payload),
            }
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || "Update failed");
          }

          summary.updated.push(employee.employeeId);
        } catch (error) {
          summary.failed.push({
            employeeId: employee.employeeId,
            name: employee.employeeName,
            error: error.message,
          });
        }
      }

      setResults(summary);
      await fetchEmployees();
      Alert.alert(
        "QuickBooks Import Complete",
        `Updated ${summary.updated.length} employee${summary.updated.length === 1 ? "" : "s"}.` +
          (summary.failed.length ? ` Failed: ${summary.failed.length}.` : "")
      );
    } finally {
      setApplying(false);
    }
  };

  const renderDetectedColumn = (field) => (
    <View key={field.key} style={styles.columnBadge}>
      <Text style={styles.columnLabel}>{field.label}</Text>
      <Text style={styles.columnValue}>{columns[field.key] || "Not found"}</Text>
    </View>
  );

  const renderPreviewRow = (row) => (
    <View key={`${row.rowNumber}-${row.sourceName}`} style={styles.previewRow}>
      <View style={styles.previewEmployee}>
        <Text style={styles.previewName}>
          {row.employee?.employeeName || row.sourceName || "Unmatched row"}
        </Text>
        <Text style={styles.previewMeta}>
          Row {row.rowNumber}
          {row.employee ? ` · ${row.employee.employeeId} · Matched by ${row.matchedBy}` : " · No match"}
        </Text>
        <Text style={styles.previewMeta}>
          QuickBooks: {row.sourceName || "-"}
          {row.employee?.email ? ` · Attendance email: ${row.employee.email}` : ""}
        </Text>
      </View>

      <View style={styles.rateGrid}>
        {FIELD_CONFIG.map((field) => (
          <View key={field.key} style={styles.rateCell}>
            <Text style={styles.rateLabel}>{field.label}</Text>
            <Text style={styles.rateValue}>{formatRate(row.updates[field.key]) || "-"}</Text>
          </View>
        ))}
      </View>
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerIcon}>
            <MaterialCommunityIcons name="file-sync-outline" size={32} color="#1B8A3D" />
          </View>
          <View style={styles.headerText}>
            <Text style={styles.title}>QuickBooks Rate Import</Text>
            <Text style={styles.subtitle}>
              Upload the weekly QuickBooks spreadsheet to merge pay and bill rates into employee records.
            </Text>
          </View>
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>Accepted Columns</Text>
          <Text style={styles.helpText}>
            The QuickBooks Detailed tab imports base pay from Earnings 1/Rate, and Employee Payroll
            Summary imports OT pay from the Overtime row. Generic files can also include Pay Rate,
            OT Pay Rate, ST Bill Rate, and OT Bill Rate columns.
          </Text>

          <TouchableOpacity
            style={[styles.primaryButton, (loadingEmployees || processingFile) && styles.disabledButton]}
            onPress={chooseFile}
            disabled={loadingEmployees || processingFile}
          >
            {processingFile ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <>
                <MaterialCommunityIcons name="microsoft-excel" size={22} color="#FFFFFF" />
                <Text style={styles.primaryButtonText}>
                  {fileName ? "Choose Another File" : "Upload QuickBooks File"}
                </Text>
              </>
            )}
          </TouchableOpacity>

          {fileName ? <Text style={styles.fileName}>Selected: {fileName}</Text> : null}
        </View>

        {Object.keys(columns).length > 0 ? (
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Detected Mapping</Text>
            <View style={styles.columnGrid}>
              {MATCH_CONFIG.map(renderDetectedColumn)}
              {FIELD_CONFIG.map(renderDetectedColumn)}
            </View>
          </View>
        ) : null}

        {rows.length > 0 ? (
          <View style={styles.card}>
            <View style={styles.summaryGrid}>
              <View style={styles.summaryBox}>
                <Text style={styles.summaryValue}>{rowsToUpdate.length}</Text>
                <Text style={styles.summaryLabel}>Ready</Text>
              </View>
              <View style={styles.summaryBox}>
                <Text style={styles.summaryValue}>{unmatchedRows.length}</Text>
                <Text style={styles.summaryLabel}>Unmatched</Text>
              </View>
              <View style={styles.summaryBox}>
                <Text style={styles.summaryValue}>{noRateRows.length}</Text>
                <Text style={styles.summaryLabel}>No Rates</Text>
              </View>
            </View>

            <TouchableOpacity
              style={[styles.applyButton, (applying || !rowsToUpdate.length) && styles.disabledButton]}
              onPress={applyUpdates}
              disabled={applying || !rowsToUpdate.length}
            >
              {applying ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.primaryButtonText}>Apply Updates</Text>
              )}
            </TouchableOpacity>

            <Text style={styles.cardTitle}>Preview</Text>
            {rows.slice(0, 50).map(renderPreviewRow)}
            {rows.length > 50 ? (
              <Text style={styles.helpText}>Showing the first 50 rows of {rows.length}.</Text>
            ) : null}
          </View>
        ) : null}

        {results ? (
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Last Import Result</Text>
            <Text style={styles.helpText}>
              Updated {results.updated.length} employee{results.updated.length === 1 ? "" : "s"}.
              {results.failed.length ? ` Failed ${results.failed.length}.` : ""}
            </Text>
            {results.failed.map((failure) => (
              <Text key={failure.employeeId} style={styles.errorText}>
                {failure.employeeId} {failure.name}: {failure.error}
              </Text>
            ))}
          </View>
        ) : null}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#e6f0e8",
  },
  container: {
    padding: 20,
    maxWidth: 1180,
    width: "100%",
    alignSelf: "center",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20,
  },
  headerIcon: {
    width: 58,
    height: 58,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
    marginRight: 14,
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: 28,
    fontWeight: "700",
    color: "#173f2f",
  },
  subtitle: {
    marginTop: 4,
    fontSize: 15,
    color: "#4A5D52",
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 10,
    padding: 18,
    marginBottom: 18,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 3,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: "700",
    color: "#173f2f",
    marginBottom: 10,
  },
  helpText: {
    fontSize: 14,
    lineHeight: 20,
    color: "#5B6B62",
    marginBottom: 14,
  },
  primaryButton: {
    alignSelf: "flex-start",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: "#1B8A3D",
    borderRadius: 8,
    paddingHorizontal: 18,
    paddingVertical: 12,
  },
  applyButton: {
    alignSelf: "flex-start",
    backgroundColor: "#173f2f",
    borderRadius: 8,
    paddingHorizontal: 18,
    paddingVertical: 12,
    marginBottom: 20,
  },
  disabledButton: {
    opacity: 0.55,
  },
  primaryButtonText: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "700",
  },
  fileName: {
    marginTop: 12,
    color: "#4A5D52",
    fontWeight: "600",
  },
  columnGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  columnBadge: {
    minWidth: 150,
    backgroundColor: "#F3F7F4",
    borderRadius: 8,
    padding: 10,
  },
  columnLabel: {
    fontSize: 12,
    color: "#607267",
    marginBottom: 4,
    textTransform: "uppercase",
  },
  columnValue: {
    fontSize: 14,
    fontWeight: "700",
    color: "#173f2f",
  },
  summaryGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 16,
  },
  summaryBox: {
    minWidth: 120,
    backgroundColor: "#F3F7F4",
    borderRadius: 8,
    padding: 12,
  },
  summaryValue: {
    fontSize: 24,
    fontWeight: "800",
    color: "#1B8A3D",
  },
  summaryLabel: {
    fontSize: 13,
    color: "#607267",
  },
  previewRow: {
    borderTopWidth: 1,
    borderTopColor: "#E4ECE6",
    paddingVertical: 12,
  },
  previewEmployee: {
    marginBottom: 8,
  },
  previewName: {
    fontSize: 15,
    fontWeight: "700",
    color: "#173f2f",
  },
  previewMeta: {
    fontSize: 12,
    color: "#607267",
    marginTop: 2,
  },
  rateGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  rateCell: {
    minWidth: 108,
    backgroundColor: "#F9FBFA",
    borderRadius: 6,
    padding: 8,
  },
  rateLabel: {
    fontSize: 11,
    color: "#607267",
  },
  rateValue: {
    marginTop: 3,
    fontSize: 14,
    fontWeight: "700",
    color: "#173f2f",
  },
  errorText: {
    color: "#B42318",
    marginTop: 6,
  },
});

export default QuickBooksImport;
